/**
 * Sinais globais (§2.4.1; FE §6.2, DE-B01). FOLHA: importa só `@preact/signals` e tipos (`import type`), para que
 * `router/` e `a11y/` possam lê-lo e escrevê-lo sem ciclo com `state/actions.ts` (§2.2.2).
 * Cada sinal tem escritor único (FE §6.6, verificado por tests/unit/architecture.test.ts); objetos dentro dos sinais
 * são imutáveis (substituir, nunca mutar) e toda escrita que muda mais de um sinal usa `batch()` (§2.4.2).
 */
import { computed, signal } from '@preact/signals';
import type { AppConfig } from '../config';
import type { CandidateNumber, IbgeCode, Office, UfCode } from '../contracts/base';
import type { ElectionEvent } from '../contracts/events';
import type { HistoryPoint } from '../contracts/history';
import type { ProbabilityDetail } from '../contracts/probability';
import type { Baseline2022, CandidateList, FirstRoundResults, Geography } from '../contracts/static';
import type { FeedState, LayoutState, MapLink, ResultsView, RouteState } from '../model/types';

// ── Tipos (§2.4.1; FE §6.4) ──────────────────────────────────────────────────────────────────────────────────
export interface TimelineState {
  /** true = acompanha o último snapshot. */
  live: boolean;
  /** Relógio DOS DADOS: instantTMin do último snapshot vivo aceito; monotônico (§10.1.2, DE-B03). */
  clockTMin: number;
  /** Instante exibido (= clockTMin ao vivo, ou o do replay). */
  displayTMin: number;
  /** Vista do instante pedido; mantém a anterior enquanto carrega (AC-2.4-04). */
  replayView: ResultsView | null;
  replayLoading: boolean;
}

export interface CandidateProfileRef {
  number: CandidateNumber;
  office: Office;
  origin: 'list' | 'search';
}

export interface TvState {
  on: boolean;
  sceneId: string | null;
  overlay: ElectionEvent | null;
}

/** Estado efêmero, nunca na URL (§5.1). */
export interface UiState {
  searchOpen: boolean;
  aboutOpen: null | 'about' | 'simulation';
  pickerOpen: boolean;
  profile: CandidateProfileRef | null;
  /** Id da lista expandida (ex.: 'abroad-countries'). */
  expanded: string | null;
  /** Destaque no mapa vindo de listas e eventos (§9.11.4). */
  mapLink: MapLink | null;
  toast: ElectionEvent | null;
  /** Aviso transitório (o aviso operacional vem de feed.notice, §6.10). */
  notice: string | null;
  /** Modalidade de teclado (§15.2). */
  keyboard: boolean;
  tv: TvState;
}

/** Relógio da fonte (DE-B03): `ms` = source.clock(); `tMin` = minutos desde 00h00 de 25/10 em Brasília. */
export interface NowState {
  ms: number;
  tMin: number;
}

/** Estáticos (§2.2.4, §14.2; DE-B01): `undefined` = carregando; `null` = ausente ou inválido. */
export interface StaticsState {
  candidates: CandidateList | null;
  geography: Geography | null;
  firstRound: FirstRoundResults | null | undefined;
  baseline: Baseline2022 | null | undefined;
}

/**
 * Contexto da rota (§5.1). Mora aqui (e não em model/types.ts) porque usa `AppConfig['features']` e `model/` não importa
 * `config` (§2.2.2); `router/types.ts` o reexporta. Escrito por `app/feedBinding.ts` (e por main.tsx no boot).
 */
export interface RouteContext {
  /** null até /static/geography.json chegar. */
  geo: Geography | null;
  /** null até /static/candidates.json chegar. */
  candidates: CandidateList | null;
  flags: AppConfig['features'];
  /** Chaves de CandidateList.governor (as UFs com 2º turno; 6 desde a FA-02, sem o RJ). */
  governorUfs: ReadonlySet<UfCode>;
  /** Janela de §10.1. */
  timeline: { startTMin: number; endTMin: number; liveTMin: number | null } | null;
  /** Líder nacional no instante exibido (resolve `cand` ausente). */
  nationalLeader: CandidateNumber | null;
}

export interface PresenceState {
  n: number;
}

// ── Valores iniciais ─────────────────────────────────────────────────────────────────────────────────────────
/**
 * Sentinela tipado de `config` (FE §6.2): cópia literal de DEFAULT_CONFIG de config.ts, que esta folha não pode importar
 * em valor. store.test.ts garante a igualdade. main.tsx grava o resultado de resolveConfig() antes do render.
 */
export const INITIAL_CONFIG: AppConfig = Object.freeze({
  mode: 'live',
  feed_origins: Object.freeze(['https://feed.mapa.voto/feed/v1/']) as string[],
  build: '0000000-000000000000',
  poll_interval_s: 15,
  election_date: '2026-10-25',
  round: 2,
  presence_origin: null,
  social_profiles: Object.freeze({ x: null, instagram: null }),
  features: Object.freeze({
    governors: true,
    abroad: true,
    municipal_map: true,
    advantage_mode: false,
    zones: false,
    replay: true,
    tv: true,
    probability: false,
    archive_1t: false,
    partner_slots: false,
    presence: false,
    photos: true,
    share_image: false,
    social_links: false,
    governor_municipal: false,
    map_intro: false,
    map_glow: false,
    commentary: false,
    replay_commentary: false,
    replay_photos: false,
    replay_bridge: false,
    commentary_timeline: false,
    site_phases: false,
    preopen_strong: false,
    math_card: false,
    early_veil: false,
    share: false,
    my_city: false,
    login_gate: false,
    capa_only: false,
  }),
});

/** Padrão de §5.1 (`DEFAULT_ROUTE`): Presidente · Brasil, modo Municípios, ao vivo. */
export const INITIAL_ROUTE: RouteState = Object.freeze({
  office: 'president',
  place: Object.freeze({ kind: 'br' }),
  mapMode: 'municipalities',
  mapCandidate: null,
  replayTMin: null,
  tvOnEntry: false,
});

/** Palco nominal do desktop; main.tsx grava o valor real (layoutMode) antes do render (M27). */
export const INITIAL_LAYOUT: LayoutState = Object.freeze({
  mode: 'desktop',
  heightClass: 'tall',
  zoom: 1,
  stageW: 1600,
  stageH: 900,
  pointerCoarse: false,
});

export const INITIAL_FEED: FeedState = Object.freeze({
  connection: 'connecting',
  source: '',
  seq: null,
  epoch: null,
  lastSuccessAt: null,
  lastChangeAt: null,
  failures: 0,
  reason: null,
  notice: null,
  online: true,
  sourceStatus: null,
  verifiedAt: null,
  tseHtTMin: null,
  meanSeqIntervalMs: null,
  allComplete: false,
});

/** Antes do primeiro snapshot não há relógio dos dados: 0 (00h00 de 25/10); effects.ts o avança. */
export const INITIAL_TIMELINE: TimelineState = Object.freeze({
  live: true,
  clockTMin: 0,
  displayTMin: 0,
  replayView: null,
  replayLoading: false,
});

export const INITIAL_UI: UiState = Object.freeze({
  searchOpen: false,
  aboutOpen: null,
  pickerOpen: false,
  profile: null,
  expanded: null,
  mapLink: null,
  toast: null,
  notice: null,
  keyboard: false,
  tv: Object.freeze({ on: false, sceneId: null, overlay: null }),
});

export const INITIAL_NOW: NowState = Object.freeze({ ms: 0, tMin: 0 });

export const INITIAL_STATICS: StaticsState = Object.freeze({
  candidates: null,
  geography: null,
  firstRound: undefined,
  baseline: undefined,
});

export const INITIAL_CTX: RouteContext = Object.freeze({
  geo: null,
  candidates: null,
  flags: INITIAL_CONFIG.features,
  governorUfs: new Set<UfCode>(),
  timeline: null,
  nationalLeader: null,
});

// ── Sinais (escritor único entre parênteses; FE §6.6) ────────────────────────────────────────────────────────
/** (main.tsx, uma vez, congelado) */
export const config = signal<AppConfig>(INITIAL_CONFIG);
/** (router/navigate.ts: navigate + hashchange) */
export const route = signal<RouteState>(INITIAL_ROUTE);
/** (main.tsx no boot e app/useLayoutMode.ts) */
export const layout = signal<LayoutState>(INITIAL_LAYOUT);
/** (app/feedBinding.ts) */
export const feed = signal<FeedState>(INITIAL_FEED);
/** (app/feedBinding.ts) */
export const liveView = signal<ResultsView | null>(null);
/** D3: o município guardado no aparelho (código IBGE); null = nenhum ou a chave `my_city` desligada. Escritor: actions.ts. */
export const myCity = signal<IbgeCode | null>(null);
/** (app/feedBinding.ts) */
export const historySeries = signal<readonly HistoryPoint[]>([]);
/** (state/actions.ts: scrubTo, goLive; state/effects.ts: só clockTMin) */
export const timeline = signal<TimelineState>(INITIAL_TIMELINE);
/**
 * (state/actions.ts; state/effects.ts: só toast e tv.overlay; router/effects.ts: fecha painéis;
 * a11y/useKeyboardModality.ts: só keyboard)
 */
export const ui = signal<UiState>(INITIAL_UI);
/** (state/effects.ts: tique de 1 s) */
export const now = signal<NowState>(INITIAL_NOW);
/** (main.tsx: candidates; app/feedBinding.ts: demais) */
export const statics = signal<StaticsState>(INITIAL_STATICS);
/** (main.tsx e app/feedBinding.ts) */
export const ctx = signal<RouteContext>(INITIAL_CTX);
/** P2 (app/feedBinding.ts, só com features.presence) */
export const presence = signal<PresenceState | null>(null);
/** `files.insights` do snapshot atual (P-27; app/feedBinding.ts): null = sem a camada de Situação incomum. */
export const insightsPath = signal<string | null>(null);
/** `files.commentary` do snapshot atual (P-32): null = comentários desligados. */
export const commentaryPath = signal<string | null>(null);
/** Sobe quando o índice dos comentários saiu de `active` com o `seq` parado (docs/DECISOES.md §75; app/feedBinding.ts). */
export const commentaryRev = signal(0);
/**
 * O `status` do índice dos comentários adotado (app/commentaryLive.tsx); null antes de ler. Com `off`, as abas da coluna
 * direita e a tela "Linha do tempo" somem, e o site fica como sem comentários (pré-mortem de 25/10, P-87).
 */
export const commentaryStatus = signal<'active' | 'paused' | 'off' | null>(null);
/** Detalhe da chance (`p/{base_seq}/probability.json`): a chance de governador por UF (P-05, P-30 item 7). */
export const probabilityDetail = signal<ProbabilityDetail | null>(null);

// ── Computeds (§2.4.1) ───────────────────────────────────────────────────────────────────────────────────────
/** Vista ao vivo ou do replay. */
export const view = computed<ResultsView | null>(() =>
  timeline.value.live ? liveView.value : timeline.value.replayView,
);
/** Estado "Simulação" do App (§2.2): continua true no replay; `origin: 'replay'` não liga. */
export const isSimulated = computed<boolean>(
  () =>
    config.value.mode === 'simulation' ||
    liveView.value?.origin === 'simulation' ||
    liveView.value?.origin === 'fixture',
);
